import { Injectable, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { UploadCtsService } from 'src/app/features/management/cts/services/upload-cts.service';

@Injectable({ providedIn: 'root' })
export class CtsProgressStore {

  private uploadService = inject(UploadCtsService);
  private messageService = inject(MessageService);

  public progress    = signal<number>(0);
  public isActive    = signal<boolean>(false);
  /** true cuando el polling terminó con completed pero el usuario aún no ha "Finalizado" */
  public isCompleted = signal<boolean>(false);
  public isError     = signal<boolean>(false);

  private timeoutRef: any = null;
  private readonly STORAGE_KEY = 'cts_migration_id';

  // El backend solo actualiza en pasos: 0 → 25 → 50 → 75 → 100
  // Usamos backoff escalonado: arranca en 6 s, sube 1.5× por cada poll
  // sin avance, hasta un máximo de 30 s.
  private _currentInterval = 6_000;
  private _lastProgress = 0;
  private _errorRetries = 0;
  private readonly BASE_INTERVAL  = 6_000;
  private readonly BACKOFF_FACTOR = 1.5;
  private readonly MAX_INTERVAL   = 30_000;
  private readonly MAX_ERROR_RETRIES = 3;

  private completionSubject = new Subject<void>();
  public completion$ = this.completionSubject.asObservable();

  private errorSubject = new Subject<void>();
  public error$ = this.errorSubject.asObservable();

  private cancelledSubject = new Subject<void>();
  public cancelled$ = this.cancelledSubject.asObservable();

  /* ============================================
     INICIAR TRACKING (cuando se lanza la carga)
  ============================================ */
  startTracking(migrationId: number): void {
    localStorage.setItem(this.STORAGE_KEY, String(migrationId));
    this.uploadService.migrationId.set(migrationId);
    this.progress.set(0);
    this._lastProgress = 0;
    this._errorRetries = 0;
    this._currentInterval = this.BASE_INTERVAL;
    this.isCompleted.set(false);
    this.isError.set(false);
    this.isActive.set(true);
    this._scheduleNext();
  }

  /* ============================================
     REANUDAR SI HAY UNA MIGRACIÓN ACTIVA
     (llamar en AppComponent.ngOnInit)
  ============================================ */
  resumeIfActive(): void {
    if (this.isActive()) return;
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (!saved) return;
    const parsedId = Number(saved);
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      localStorage.removeItem(this.STORAGE_KEY);
      return;
    }
    this.uploadService.migrationId.set(parsedId);
    this._lastProgress = this.progress();
    this._errorRetries = 0;
    this._currentInterval = this.BASE_INTERVAL;
    this.isActive.set(true);
    this.isCompleted.set(false);
    this.isError.set(false);
    this._poll();
  }

  /* ============================================
     POLLING ADAPTATIVO
     Objetivo: ~1 request por cada 1% de avance
  ============================================ */
  private _scheduleNext(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
    this.timeoutRef = setTimeout(() => this._poll(), this._currentInterval);
  }

  private _poll(): void {
    this.uploadService.getProgress().subscribe({
      next: (resp) => {
        if (!this.isActive()) return;
        this._errorRetries = 0;

        const pct = resp.data?.porcentaje ?? 0;
        const delta = pct - this._lastProgress;

        if (delta > 0) {
          // Hubo avance: reiniciar al intervalo base para el siguiente paso
          this._currentInterval = this.BASE_INTERVAL;
        } else {
          // Sin avance: backoff moderado hasta el máximo
          this._currentInterval = Math.min(this._currentInterval * this.BACKOFF_FACTOR, this.MAX_INTERVAL);
        }

        this._lastProgress = pct;
        this.progress.set(pct);

        const estado = resp.data?.estado as string | undefined;
        if (estado === 'completed') {
          this._onComplete();
        } else if (estado === 'error' || estado === 'failed') {
          this._onError();
        } else if (estado === 'pending' || estado === 'validated' || estado === 'no file upload') {
          // Migración nunca enviada a /cargar: limpiar tracking obsoleto
          this.stopTracking();
          this.cancelledSubject.next();
        } else {
          this._scheduleNext();
        }
      },
      error: (err: any) => {
        if (!this.isActive()) return;

        // 404 / 400 / 422 = migración inexistente o inválida → limpiar localStorage y salir
        if (err?.status === 404 || err?.status === 400 || err?.status === 422) {
          this.stopTracking();
          this.cancelledSubject.next();
          return;
        }

        this._errorRetries++;
        if (this._errorRetries >= this.MAX_ERROR_RETRIES) {
          this._onError();
          return;
        }

        this._currentInterval = Math.min(this._currentInterval * 2, this.MAX_INTERVAL);
        this._scheduleNext();
      }
    });
  }

  private _onComplete(): void {
    if (!this.isActive()) return;
    this._stopPolling();
    this.progress.set(100);
    this.isActive.set(false);
    this.isCompleted.set(true);
    this.isError.set(false);
    localStorage.removeItem(this.STORAGE_KEY);
    this.completionSubject.next();
    this.messageService.add({
      severity: 'success',
      summary: 'Carga CTS completada',
      detail: 'Todos los archivos CTS se cargaron correctamente.',
      life: 6000
    });
  }

  private _onError(): void {
    if (!this.isActive()) return;
    this._stopPolling();
    this.isActive.set(false);
    this.isCompleted.set(false);
    this.isError.set(true);
    localStorage.removeItem(this.STORAGE_KEY);
    this.errorSubject.next();
    this.messageService.add({
      severity: 'error',
      summary: 'Error en carga CTS',
      detail: 'El proceso de carga falló. Verifique el archivo e intente nuevamente.',
      life: 6000
    });
  }

  private _stopPolling(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
      this.timeoutRef = null;
    }
    this.isActive.set(false);
  }

  /**
   * Oculta la barra flotante pero mantiene el migrationId en localStorage
   * para que al volver a la página se pueda reanudar el polling.
   */
  dismiss(): void {
    this._stopPolling();
    this.isActive.set(false);
    /* NO borra localStorage ni progress — permite reanudar */
  }

  /** Cancela completamente: detiene polling, limpia estado y localStorage */
  stopTracking(): void {
    this._stopPolling();
    this.progress.set(0);
    this.isCompleted.set(false);
    this.isError.set(false);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /** Llamar después de que el usuario pulsa "Finalizar" en la página */
  acknowledgeComplete(): void {
    this.isCompleted.set(false);
    this.isError.set(false);
    this.progress.set(0);
  }
}
